import { fromBaseRecord, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { readItemField } from './item-field-reader';

export function inventoryDefinitionLayerOrder(names: readonly string[]): ReadonlyMap<string, number> {
    if (names.some(name => typeof name !== 'string' || name === '') || new Set(names).size !== names.length)
        throw new TypeError('Invalid definition layer order');
    return new Map(names.map((name, index) => [name, index]));
}

/** The native mirror window over constructed own layers. The raw catalog's
 * iteration order is supplied by the same SELECT snapshot, never a JS sort. */
export function finalizeInventoryDefinitionLayerSurface(source: Row, layerOrder?: readonly string[], leading: readonly string[] = [],
    registered?: Readonly<Record<string, unknown>>, preparedOrder?: ReadonlyMap<string, number>): Row {
    const result = { ...source }, layers = { ...result.item_layer_types as Row };
    for (const name of layerOrder ?? []) {
        if (!Object.prototype.hasOwnProperty.call(result, name)) continue;
        if (!Object.prototype.hasOwnProperty.call(layers, name)) layers[name] = result[name];
        delete result[name];
    }
    for (const name of ['v_current', 'v_max', 'v_temp', 'v_max_temp']) {
        if (leading.includes(name) || !Object.prototype.hasOwnProperty.call(layers, name)) continue;
        const scalar = readItemField(layers, name);
        const computed = fromBaseRecord(result.computed) ? result.computed : {};
        const vitality = fromBaseRecord(computed.vitality) ? computed.vitality : {};
        result.computed = { ...computed, vitality: { ...vitality,
            ...(readItemField(vitality, name) === null ? { [name]: scalar } : {}) } };
        delete layers[name];
    }
    for (const [name, entry] of Object.entries(layers)) {
        if (!leading.includes(name) && (entry === null || entry === '' || Array.isArray(entry) && entry.length === 0))
            delete layers[name];
    }
    if (layerOrder) {
        const order = preparedOrder ?? inventoryDefinitionLayerOrder(layerOrder), first = new Map<string, number>();
        for (const name of leading) if (!first.has(name)) first.set(name, first.size);
        // mirror_layers_into_bucket only moves registered layers. Intrinsic
        // enchantments also emit legacy fields absent from the active registry.
        for (const name of Object.keys(layers).filter(name => !first.has(name) && !order.has(name))) {
            if (!registered || Object.prototype.hasOwnProperty.call(registered, name))
                throw new FromBaseStageMissing('definition-layer-order', `unknown-layer:${name}`);
            if (!Object.prototype.hasOwnProperty.call(result, name)) result[name] = [layers[name]];
            delete layers[name];
        }
        const rank = (name: string) => first.get(name) ?? first.size + order.get(name)!;
        result.item_layer_types = Object.fromEntries(Object.keys(layers).sort((a, b) => rank(a) - rank(b))
            .map(name => [name, layers[name]]));
    } else result.item_layer_types = layers;
    if (!Object.keys(result.item_layer_types as Row).length) delete result.item_layer_types;
    return result;
}
